// src/services/socket.js
//
// 🟢 SIGNALING SOCKET — the web joins the SAME socket.io backend the phone uses.
// Without this the web could only write to Firestore, which meant:
//   • no fast relay to the recipient's device
//   • NO PUSH NOTIFICATION (the server sends FCM from its `new-message` handler)
//   • no server-assigned `seq`
// i.e. messages sent from the web effectively arrived only when the recipient
// happened to open the app. Connecting here fixes all of it.
//
// 🟥 THAT USED TO BE ALL THE HANDSHAKE NEEDED, AND THAT WAS THE PROBLEM. The
//    server took `handshake.query.userId` on trust — a query-string parameter,
//    believed. The handshake now also carries a Firebase ID token the server
//    can actually verify, minted fresh for every connection attempt.
//
// 🟢 GUESTS WORK THE SAME WAY. A guest is a Firebase ANONYMOUS account, not an
//    unauthenticated one — guestSession.js already signs them in and already
//    sends their token on every HTTP call. The server accepts the anonymous
//    provider deliberately (note the asymmetry with guestLinks.js, which
//    REJECTS non-anonymous tokens for guest routes).

import { io } from 'socket.io-client';
import { auth } from '../config/firebase';
import { SENT_FROM, cachedWebDeviceId } from './sendDoc';

/* the last token we presented — what a failed refresh falls back to, so a bad
   moment degrades to "slightly stale" rather than "anonymous" */
let _lastToken = null;

// 🟥 THE WRONG-SERVER BUG (root cause of "web messages don't appear on my phone
//    until re-enter" AND "recipient gets no notification / stuck on one grey tick").
//    This used to point at allo-backend-…me-central1 — an OLD backend in another
//    region that no phone connects to. The phones live on the SIGNALING servers
//    (config/backend.ts in the app): every new-message the web emitted therefore
//    landed on a server with zero phones attached — no self-fanout to your own
//    phone's open chat, no relay to the recipient, no FCM push, no delivered
//    watermark. The Firestore write still happened, which is why the message DID
//    appear after exit/re-enter (the open load reads the collection).
//    Mirrors the phone's dev/prod split: `npm start` (development) joins the same
//    dev server a Metro/debug phone build uses; a production web build joins prod,
//    like a release phone. REACT_APP_BACKEND_URL still overrides both.
const DEV_BACKEND  = 'https://signaling-server-397203671414.us-central1.run.app';
const PROD_BACKEND = 'https://signaling-server-prod-397203671414.us-central1.run.app';
const BACKEND = process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === 'development' ? DEV_BACKEND : PROD_BACKEND);

// 🟢 Exported so the media uploader hits the SAME backend the socket is on — the R2
//    presign route lives on that server, and a mismatch here would mint upload URLs
//    from a box no phone talks to (exactly the wrong-server bug described above).
export const BACKEND_URL = BACKEND;

let _socket = null;
let _userId = null;

export function initSocket(userId, deviceId) {
  if (!userId) return null;
  if (_socket && _userId === userId) {
    if (!_socket.connected) _socket.connect();
    return _socket;
  }
  if (_socket) { try { _socket.disconnect(); } catch {} _socket = null; }
  _userId = userId;
  _socket = io(BACKEND, {
    /* 🟥 A FUNCTION, CALLED BEFORE EVERY ATTEMPT. An ID token lasts an hour;
       this socket reconnects forever and Cloud Run severs it hourly, so a token
       captured once would work until the first reconnect after expiry and then
       never again. Deadlined, because a handshake whose callback never fires
       never connects — on a bad network, connecting without a token beats not
       connecting while the server is still observing. */
    auth: (cb) => {
      let done = false;
      const finish = (t) => { if (done) return; done = true; cb(t ? { token: t } : {}); };
      const timer = setTimeout(() => finish(_lastToken), 3000);
      Promise.resolve()
        .then(() => auth?.currentUser?.getIdToken())
        .then((t) => { clearTimeout(timer); if (t) _lastToken = t; finish(t || _lastToken); })
        .catch(() => { clearTimeout(timer); finish(_lastToken); });
    },
    transports: ['websocket'],
    upgrade: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 8000,
    // 🟢 deviceId in the handshake → the server's per-DEVICE routing (calls, ring
    //    dismissal, "answered elsewhere") can address this browser like any phone.
    query: deviceId ? { userId, deviceId } : { userId },
  });
  _socket.on('connect', () => {
    console.log('🔌 [socket] connected', _socket.id);
    // mirror the phone: announce ourselves so the server maps uid → socket
    try { _socket.emit('user-joined', { userId, userData: { userId, platform: 'web', deviceId: deviceId || null } }); } catch {}
  });
  _socket.on('disconnect', (r) => console.log('🔌 [socket] disconnected:', r));
  /* 🔐 the server could not verify this socket at the handshake: prove it now (calls, deletes,
     group actions — and everything a guest does — need a verified token) */
  _socket.on('auth-required', () => {
    const s = _socket;
    Promise.resolve().then(() => auth?.currentUser?.getIdToken()).then((t) => {
      const tok = t || _lastToken;
      if (tok && s) { _lastToken = tok; try { s.emit('socket-auth', { token: tok }); } catch (_) {} }
    }).catch(() => {});
  });
  /* 🟥 THE ORDER ACK. The server assigns each message a per-chat `seq` — the key
     BOTH clients sort by — and reports it back on this event. It does NOT write
     it to the message document; each sender writes its own (the phone does
     exactly this in HomeScreen's `message-seq` handler). The web never listened,
     so every message sent from a browser carried no seq for the rest of its
     life, and the app sorted it by timestamp among neighbours sorted by seq.
     Listeners register through onMessageSeq() so the Firestore write can live
     with the other message code instead of in the transport. */
  _socket.on('message-seq', (ack) => {
    _seqListeners.forEach((fn) => { try { fn(ack); } catch {} });
  });
  return _socket;
}

/* ── the seq ack, for whoever wants to persist it ───────────────────────────
   A Set, not one callback: registering twice must not silently unhook the
   first. Returns an unsubscribe. */
const _seqListeners = new Set();
export function onMessageSeq(fn) {
  if (typeof fn !== 'function') return () => {};
  _seqListeners.add(fn);
  return () => _seqListeners.delete(fn);
}

export function getSocket() { return _socket; }

export function closeSocket() {
  if (_socket) { try { _socket.disconnect(); } catch {} }
  _socket = null; _userId = null;
}

/** Relay a freshly-written message so the server pushes + relays it (same payload
 *  shape the mobile app emits). Fire-and-forget. */
export function relayNewMessage({ chatId, messageId, fromUserId, participants, isGroup, cloudText, perDeviceKeys, senderDeviceId, replyTo, clientMsgId, extra, mentions, mentionsEveryone }) {
  const s = _socket;
  // 🟢 loud, not silent: a missing/dead socket here means the message will ONLY exist
  //    in Firestore — no push, no fanout — which is invisible-failure territory.
  if (!s) { console.warn('[socket] relayNewMessage: no socket — message written to Firestore only'); return; }
  if (!s.connected) console.warn('[socket] relayNewMessage: socket not connected — relying on socket.io buffering');
  const base = {
    fromUserId,
    message: cloudText,
    nonce: null,
    chatId,
    messageId: String(messageId),
    clientMsgId: clientMsgId || String(messageId),
    ...(perDeviceKeys && { perDeviceKeys, senderDeviceId }),
    ...(replyTo && { replyTo: typeof replyTo === 'string' ? replyTo : JSON.stringify(replyTo) }),
    // 🟢 MEDIA: imageUrl / imagePerDeviceKeys / images[] / batchPerDeviceKeys / thumbs…
    //    The server relays `{...data}` verbatim, so anything added here reaches the
    //    recipient's live socket handler with the exact field names the app expects.
    /* 🖥📱 where it was typed — my phone draws a globe on it, and accepts a plaintext
       kind (a flight card) from this browser as "my other device" rather than an echo.
       See services/messageOrigin and sendDoc. */
    sentFrom: SENT_FROM,
    ...(!perDeviceKeys && !(extra && extra.senderDeviceId) && cachedWebDeviceId()
      ? { senderDeviceId: cachedWebDeviceId() } : {}),
    ...(extra || {}),
  };
  const others = (participants || []).filter((u) => u && u !== fromUserId);
  try {
    /* ══ 🤖 A MENTION HAS TO PIERCE A MUTED GROUP, AND ONLY THE SERVER CAN DO IT
       — it is the only thing that decides whether a push goes out, and it cannot
       read the message to see the "@". So each envelope, which is already addressed
       to one member, says whether THAT member was named. One boolean about a uid
       this call is already fanning out to: not the text, not the name, not who else
       was mentioned. The app's send path does exactly this; see ChatModal. */
    const _named = Array.isArray(mentions) ? mentions : [];
    /* 🟥 @everyone IS EVERY ENVELOPE, and it was decided that it pierces a mute
       the same way a personal mention does — so it is the loudest thing this
       function can send. Whether the sender was ALLOWED to send it is settled where
       the picker is, by group size and admin rights; by here it is already true. */
    const _all = !!mentionsEveryone;
    if (isGroup) others.forEach((uid) => s.emit('new-message', {
      ...base, toUserId: uid, isGroup: true,
      ...((_all || _named.includes(uid)) ? { mentionedYou: true } : {}),
    }));
    else if (others[0]) s.emit('new-message', { ...base, toUserId: others[0], isGroup: false });
  } catch (e) {
    console.warn('[socket] relay failed:', e?.message);
  }
}

/** Read receipt over the socket — the server is the primary writer of readUpTo. */
export function relaySeen({ chatId, messageId, senderId, recipientId, type, messageMillis, messageSeq }) {
  const s = _socket;
  if (!s) return;
  try {
    s.emit('report-seen', { chatId, messageId, senderId, recipientId, type: type || 'text', messageMillis, messageSeq: messageSeq ?? null });
  } catch {}
}

export function relayDelivered({ chatId, messageId, senderId, recipientId, type, messageMillis, messageSeq }) {
  const s = _socket;
  if (!s) return;
  try {
    s.emit('report-delivered', { chatId, messageId, senderId, recipientId, type: type || 'text', messageMillis, messageSeq: messageSeq ?? null });
  } catch {}
}
